import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { api, money, unitPrice, quantity, date, DOC_TYPE_LABEL, PAYMENT_LABEL } from '../api.js';
import { amountInWords } from '../numberWords.js';
import { dobaviDni, vatRateOf, opisanieNaReda, slivaniRedove } from '../printLines.js';
import { isJspmAvailable, getInstalledPrinters, getPapers, printElements } from '../jspm.js';
import { useMailSend } from '../components/MailSend.jsx';

/*
 * Принтерът и хартията за етикети — помнят се на компютъра. По
 * подразбиране „etiketen" / „51x26" — „label1" на етикетния принтер в
 * офиса няма и с нея не излизаше нищо (01.10.2026).
 */
const LS_PRINTER = 'tefter.labelPrinter';
const LS_PAPER = 'tefter.labelPaper';
function procheti(key, dflt) {
  try { return localStorage.getItem(key) || dflt; } catch { return dflt; }
}
function zapishi(key, v) {
  try { localStorage.setItem(key, v); } catch { /* няма */ }
}

/**
 * Печатен изглед на документ.
 *
 * `?form=` избира формата: invoice (фактура), receipt (стокова разписка),
 * proforma (проформа фактура). По подразбиране — фактура, ако документът
 * има номер на фактура, иначе стокова разписка.
 */
export default function PrintDocument() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const [doc, setDoc] = useState(null);
  const [company, setCompany] = useState(null);
  const [error, setError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [labelModalOpen, setLabelModalOpen] = useState(false);
  const [labelQty, setLabelQty] = useState({});
  const [labelPrinter, setLabelPrinter] = useState(() => procheti(LS_PRINTER, 'etiketen'));
  const [labelPaper, setLabelPaper] = useState(() => procheti(LS_PAPER, '51x26'));
  const [printers, setPrinters] = useState(null); // null = не е питано; [] = няма JSPM
  const [papers, setPapers] = useState([]);
  const [labelBusy, setLabelBusy] = useState(false);
  const [labelMsg, setLabelMsg] = useState(null);

  useEffect(() => {
    Promise.all([api.get(`/api/documents/${id}`), api.get('/api/settings')])
      .then(([d, s]) => { setDoc(d); setCompany(s.company ?? {}); })
      .catch((e) => setError(e.message));
  }, [id]);

  /*
   * Отпечатването на етикети сменя за миг страницата на дребен формат
   * (56×26мм) — след затваряне на диалога за печат (или отказ от него)
   * винаги трябва да се върне към нормалния А4 изглед, иначе следващият
   * печат на фактура излиза накриво.
   */
  /*
   * „Фактура" / „Проформа" / „ППП" в лентата отварят вида И веднага
   * печата (01.10.2026) — иначе „ППП" само сменяше изгледа и изглеждаше,
   * че не печата. `?print=1` се маха след първия печат, за да не печата
   * пак при презареждане.
   */
  const [, setParamsPrint] = useSearchParams();
  useEffect(() => {
    if (!doc || params.get('print') !== '1') return;
    const next = new URLSearchParams(params);
    next.delete('print');
    setParamsPrint(next, { replace: true });
    // Изчаква снимките (лого, банер), за да излязат на хартията.
    setTimeout(() => window.print(), 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [doc]);

  useEffect(() => {
    function backToNormal() { document.body.classList.remove('printing-labels'); }
    window.addEventListener('afterprint', backToNormal);
    return () => { window.removeEventListener('afterprint', backToNormal); backToNormal(); };
  }, []);

  /*
   * Фактура/Проформа без взет номер на фактура не съществуват — не може
   * да се даде на клиента такъв документ. Проверката пази и при пряк
   * адрес в браузъра, не само чрез изгасените копчета в лентата.
   */
  const requestedForm = params.get('form') ?? (doc?.invoice_no ? 'invoice' : 'receipt');
  const form = (['invoice', 'proforma'].includes(requestedForm) && !doc?.invoice_no)
    ? 'receipt' : requestedForm;

  // „Мейл“ изпраща точно документа, отворен на екрана (form).
  const mail = useMailSend({
    endpoint: `/api/documents/${id}/send-mail`,
    body: { form },
    email: doc?.partner_email,
    partnerId: doc?.partner_id,
    partnerName: doc?.partner_name,
    viewHref: `/api/documents/${id}/pdf?form=${form}`,
    onEmailSaved: (value) => setDoc((d) => ({ ...d, partner_email: value })),
  });

  if (error) return <div className="error" style={{ margin: 20 }}>{error}</div>;
  if (!doc) return <div className="empty">Зареждане…</div>;

  /*
   * Ръчна поправка на „Наименование" — не пипа истинския ред на
   * продажбата, само какво пише на хартия. Празно връща автоматичното
   * описание. Записва веднага (при напускане на полето), без отделно
   * копче „Запази" — редакцията е рядка и точкова, не иска нов екран.
   */
  async function saviOpisanie(itemId, text) {
    const res = await api.patch(`/api/documents/${id}/items/${itemId}/print-description`, { text });
    setDoc((d) => ({
      ...d,
      items: d.items.map((it) => (it.id === itemId ? { ...it, print_description: res.printDescription } : it)),
    }));
  }


  /*
   * Етикетите се броят по РЕД, не по документ — операторът маркира
   * колко копия иска за всяка стока поотделно (напр. 2 от Cyan, 1 от
   * Magenta), не един общ брой за цялата продажба.
   */
  const labelItems = doc.items.filter((it) => !Number(it.is_cancelled));
  // По подразбиране по 1 етикет на ред (01.10.2026); подаръчните редове
  // (изразходван тонер, чип) — 0, те не се носят отделно.
  const etiketiPoRed = (it) => labelQty[it.id] ?? (Number(it.is_gift) ? 0 : 1);
  const labelSheets = [];
  for (const it of labelItems) {
    const n = Number(etiketiPoRed(it));
    for (let i = 0; i < n; i++) labelSheets.push(it);
  }
  // Едро → без фирмено име на етикета; липсващо ниво (без партньор, брой
  // продажба) се третира като дребно — същото по подразбиране като
  // „Физическо лице" по-горе.
  const labelShowCompany = doc.partner_price_level !== 'wholesale';

  /*
   * Етикетите — през JSPrintManager, направо на етикетния принтер, както
   * в Деливери (01.10.2026). Прозорецът на браузъра помни последния
   * принтер и етикет излизаше на А4. Той остава само ако JSPM липсва на
   * този компютър. При изтекло време НЕ се пада към него — заданието
   * най-вероятно вече е отпечатано, а вторият опит би го дублирал.
   */
  async function printLabels() {
    if (labelSheets.length === 0) return;
    setLabelBusy(true);
    setLabelMsg(null);
    try {
      if (await isJspmAvailable()) {
        const area = document.querySelector('.label-print-area');
        area.classList.add('za-snimka');
        try {
          await printElements([...area.querySelectorAll('.label-sheet')], labelPrinter, labelPaper);
          setLabelModalOpen(false);
        } catch (err) {
          // Без повторен опит през браузъра — може вече да е излязъл. Но
          // грешката се казва, вместо попъпът да се затвори безмълвно.
          console.warn('[JSPM] Етикетите:', err);
          setLabelMsg(`${err.message} — принтер „${labelPrinter}", хартия „${labelPaper}".`);
        } finally {
          area.classList.remove('za-snimka');
        }
        return;
      }
      // Без JSPM — обикновеният прозорец за печат, както досега.
      setLabelModalOpen(false);
      document.body.classList.add('printing-labels');
      requestAnimationFrame(() => window.print());
    } catch (err) {
      setLabelMsg(err.message);
    } finally {
      setLabelBusy(false);
    }
  }

  // Връщането от клиент е кредитно известие (06.10.2026) — сумата му се
  // пази положителна, затова не стига само знакът.
  const isCredit = doc.doc_type === 'sale_return' || Number(doc.total) < 0;
  /*
   * Анулираните редове не се печатат.
   *
   * `GET /api/documents/:id` връща всички редове, включително анулираните
   * (т. 12 иска те да се пазят в базата) — но клиентът не бива да вижда
   * нито да плаща нещо, вързано с ред, който е бил махнат. Пропуснат ли се
   * тук, сборът от таблицата спира да съвпада с данъчната основа отдолу,
   * която вече е сметната само от активните редове.
   *
   * Редовете на нулева цена (подарък) не излизат на фактурата — тя не
   * пипа сумите (те и без това не допринасят нищо към данъчната основа),
   * само не заема място на хартия за нещо, за което не се плаща.
   */
  const printItems = slivaniRedove(
    doc.items.filter((it) => !Number(it.is_cancelled) && Number(it.line_subtotal) !== 0)
  );

  const title = {
    invoice: isCredit ? 'КРЕДИТНО ИЗВЕСТИЕ' : 'ФАКТУРА',
    proforma: 'ПРОФОРМА ФАКТУРА',
    // Връщане/сторно без фактура (13.10.2026) — протокол за връщане.
    receipt: isCredit ? 'ПРОТОКОЛ ЗА ВРЪЩАНЕ' : 'ПРИЕМО-ПРЕДАВАТЕЛЕН ПРОТОКОЛ',
  }[form] ?? 'ДОКУМЕНТ';

  const number = form === 'receipt' ? doc.doc_number : (doc.invoice_no ?? doc.doc_number);
  const docDate = form === 'receipt' ? doc.doc_date : (doc.invoice_date ?? doc.doc_date);

  return (
    <>
      <div className="print-toolbar">
        <button className="primary" onClick={() => window.print()}>Печат</button>
        <FormLink id={id} form="invoice" current={form} disabled={!doc.invoice_no}>Фактура</FormLink>
        <FormLink id={id} form="proforma" current={form} disabled={!doc.invoice_no}>Проформа</FormLink>
        <FormLink id={id} form="receipt" current={form}>ППП</FormLink>
        <button className={editing ? 'primary' : ''} onClick={() => setEditing((v) => !v)}>
          {editing ? 'Готово' : 'Редакция'}
        </button>
        <button onClick={() => { setLabelQty({}); setLabelModalOpen(true); }}>Етикет</button>
        {mail.button}
        {/* Изтегляне на текущата форма като PDF (06.10.2026) — същият
            изглед като „Печат“, направен на сървъра. */}
        <a className="button" href={`/api/documents/${id}/pdf?form=${form}`} download>PDF</a>
        <div style={{ flex: 1 }} />
        <button onClick={() => window.close()}>Затвори</button>
      </div>

      {mail.notice}
      {mail.modals}

      {labelModalOpen && (
        <div className="modal-backdrop" onClick={() => setLabelModalOpen(false)}>
          <div className="modal" style={{ maxWidth: 480 }} onClick={(e) => e.stopPropagation()}>
            <div className="card-head">Печат на етикети</div>
            <p className="muted" style={{ margin: '4px 0 6px' }}>
              Изберете за кой ред колко етикета да се отпечатат (56×26 мм).
            </p>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap', marginBottom: 10, fontSize: 12 }}>
              <span className="muted">Принтер:</span>
              {printers && printers.length > 0 ? (
                <select value={labelPrinter} onChange={(e) => {
                  setLabelPrinter(e.target.value); zapishi(LS_PRINTER, e.target.value);
                  getPapers(e.target.value).then(setPapers);
                }}>
                  {!printers.includes(labelPrinter) && <option value={labelPrinter}>{labelPrinter} (няма го)</option>}
                  {printers.map((p) => <option key={p} value={p}>{p}</option>)}
                </select>
              ) : (
                <strong>{labelPrinter}</strong>
              )}
              <span className="muted">хартия:</span>
              {papers.length > 0 ? (
                <select value={papers.includes(labelPaper) ? labelPaper : ''}
                  onChange={(e) => { setLabelPaper(e.target.value); zapishi(LS_PAPER, e.target.value); }}>
                  {!papers.includes(labelPaper) && <option value="">{labelPaper} (няма я — ще се вземе 51x26)</option>}
                  {papers.map((x) => <option key={x} value={x}>{x}</option>)}
                </select>
              ) : (
                <strong>{labelPaper}</strong>
              )}
              {printers === null && (
                <button className="link" onClick={() => {
                  getInstalledPrinters().then(setPrinters);
                  getPapers(labelPrinter).then(setPapers);
                }}>смени</button>
              )}
              {printers && printers.length === 0 && (
                <span className="muted">JSPrintManager не е свързан — ще излезе прозорецът за печат</span>
              )}
            </div>
            {labelMsg && <div className="error">{labelMsg}</div>}
            <table className="compact" style={{ width: '100%' }}>
              <thead>
                <tr><th>Ред</th><th style={{ width: '24mm' }}>Бр. етикети</th></tr>
              </thead>
              <tbody>
                {labelItems.map((it) => (
                  <tr key={it.id}>
                    <td>
                      {opisanieNaReda(it)}
                      {it.note && <span className="muted"> — {it.note}</span>}
                    </td>
                    <td>
                      <input
                        type="number"
                        min="0"
                        style={{ width: '20mm' }}
                        value={etiketiPoRed(it)}
                        onChange={(e) => setLabelQty((m) => (
                          { ...m, [it.id]: Math.max(0, Number(e.target.value) || 0) }
                        ))}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ display: 'flex', gap: 8, marginTop: 12, justifyContent: 'flex-end' }}>
              <button onClick={() => setLabelModalOpen(false)}>Отказ</button>
              <button className="primary" disabled={labelSheets.length === 0 || labelBusy} onClick={printLabels}>
                {labelBusy ? 'Печат…' : `Печат (${labelSheets.length})`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Отделен печатен слой за етикети — скрит извън печат на етикети
          (виж .printing-labels в print.css). Държи се винаги в DOM-а, за
          да е готов веднага щом бутонът „Печат" в попъпа смени класа на
          body-то и поиска window.print(). */}
      <div className="label-print-area">
        {labelSheets.map((it, i) => (
          <div className="label-sheet" key={i}>
            {labelShowCompany && <div className="label-company">Доктор Принтер ЕООД</div>}
            <div className="label-client">{doc.partner_name ?? 'Физическо лице'}</div>
            {it.note && <div className="label-note">{it.note}</div>}
            <div className="label-foot">
              <span>{date(doc.doc_date)}</span>
              <span>052 989 334</span>
            </div>
          </div>
        ))}
      </div>

      <div className="print-page s-baner">
        <div className="print-letterhead">
          <div>
            <img src="/brand/dp-logo.jpg" alt="Доктор Принтер" className="print-logo" />
            {form !== 'receipt' && <div className="print-original">ОРИГИНАЛ</div>}
          </div>
          <div className="print-contacts">
            <div>гр. Варна, ул. Батак 7</div>
            <div>тел. 052 989 334, 052 310 520, 0878 727 397</div>
            <div>Teams: doctor_printer</div>
            <div>office@doctorprinter.bg · www.doctorprinter.bg</div>
          </div>
        </div>

        <div className="print-title">
          <h1>{title}</h1>
          <div className="sub">
            № {number} / {date(docDate)}
          </div>
          {/* Кредитното известие е „към фактура № … от …“ (чл. 115 ЗДДС).
              Ако продажбата е била само с касова бележка — към нея и УНП-то ѝ. */}
          {isCredit && form === 'invoice' && (doc.credit_for ?? []).length > 0 && (
            <div className="sub">
              {doc.credit_for.map((s, i) => (
                <span key={s.id}>
                  {i > 0 && ', '}
                  {s.invoice_no
                    ? <>към фактура № {s.invoice_no} от {date(s.invoice_date)}</>
                    : <>към продажба № {s.doc_number} от {date(s.doc_date)}{s.unp ? `, УНП ${s.unp}` : ''}</>}
                </span>
              ))}
            </div>
          )}
          {/* Сторно на цяла продажба (13.10.2026) — към коя. */}
          {doc.status === 'storno' && doc.storno_of_number && (
            <div className="sub">сторно на продажба № {doc.storno_of_number}</div>
          )}
        </div>

        <div className="print-head">
          <div className="party">
            <h2>Получател</h2>
            <div className="name">{doc.partner_name ?? 'Физическо лице'}</div>
            <Row k="ЕИК" v={doc.partner_eik} />
            <Row k="ДДС №" v={doc.partner_vat} />
            <Row k="Адрес" v={[doc.partner_city, doc.partner_address].filter(Boolean).join(', ')} />
            <Row k="МОЛ" v={doc.partner_mol} />
          </div>

          <div className="party">
            <h2>Доставчик</h2>
            <div className="name">{company.name}</div>
            <Row k="ЕИК" v={company.eik} />
            <Row k="ДДС №" v={company.vat_number} />
            <Row k="Адрес" v={[company.city, company.address].filter(Boolean).join(', ')} />
            <Row k="МОЛ" v={company.mol} />
          </div>
        </div>

        {/* Цената и стойността по редовете са БЕЗ ДДС: редовете дават
            данъчната основа, а данъкът се начислява отдолу върху сбора
            им. Смятат се от основата на реда, защото колоната „цена"
            пази сумата с данъка при продажба и при пренесените
            доставки. */}
        <table className="print-lines">
          <thead>
            <tr>
              <th style={{ width: '8mm' }}>№</th>
              <th>Наименование</th>
              <th style={{ width: '16mm' }}>Мярка</th>
              <th className="num" style={{ width: '20mm' }}>Кол.</th>
              <th className="num" style={{ width: '26mm' }}>Ед. цена</th>
              <th className="num" style={{ width: '28mm' }}>Стойност</th>
            </tr>
          </thead>
          <tbody>
            {printItems.map((it, i) => (
              <tr key={it.id}>
                <td>{i + 1}</td>
                <td>
                  {/* Полето за редакция е само на екрана — при печат
                      винаги излиза текстът, дори операторът да е забравил
                      да натисне „Готово" преди „Печат". */}
                  <span className="print-desc-text" style={editing ? { display: 'none' } : undefined}>
                    {opisanieNaReda(it)}
                  </span>
                  {editing && (
                    <input
                      className="print-desc-edit"
                      defaultValue={opisanieNaReda(it)}
                      placeholder="автоматично"
                      onBlur={(e) => {
                        if (e.target.value !== opisanieNaReda(it)) saviOpisanie(it.id, e.target.value);
                      }}
                    />
                  )}
                  {it.note && <span className="line-note"> — {it.note}</span>}
                </td>
                <td>{it.unit}</td>
                <td className="num">{quantity(it.quantity)}</td>
                <td className="num">{unitPrice(it, doc.currency)}</td>
                <td className="num">{money(it.line_subtotal, doc.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        <div className="print-totals">
          <div><span>Данъчна основа</span><span>{money(doc.subtotal, doc.currency)}</span></div>
          <div><span>ДДС {vatRateOf(doc.items)}%</span><span>{money(doc.vat_total, doc.currency)}</span></div>
          <div className="grand"><span>Общо за плащане</span><span>{money(doc.total, doc.currency)}</span></div>
        </div>

        <div className="in-words">
          <span className="k">Словом: </span>
          {amountInWords(doc.total, doc.currency)}
        </div>

        <div className="print-meta">
          <div><span className="k">Начин на плащане: </span>{PAYMENT_LABEL[doc.payment_method]}</div>
          <div><span className="k">Дата на данъчното събитие: </span>{date(doc.doc_date)}</div>
          {/* Срокът на отложеното плащане — винаги като крайна дата, не
              като брой дни: денят, до който трябва да е преведена сумата,
              не изисква операторът да смята наум. Ако няма изрично зададен
              падеж на документа, се извежда от условието на партньора
              (partner.credit_days) — същата формула като в „Длъжници". */}
          {doc.payment_method === 'bank' && (
            <div>
              <span className="k">Срок на плащане до: </span>
              {date(doc.due_date ?? dobaviDni(doc.doc_date, doc.partner_credit_days))}
            </div>
          )}
          {doc.unp && <div><span className="k">УНП: </span>{doc.unp}</div>}
        </div>

        {doc.payment_method === 'bank' && company.bank_iban && (
          <div className="print-meta">
            <div><span className="k">Банкова сметка: </span>{company.bank_iban}</div>
          </div>
        )}

        {doc.note && <div className="in-words" style={{ whiteSpace: 'pre-line' }}>{doc.note}</div>}

        <div className="signatures">
          <div>
            <div className="line" />
            Получил
          </div>
          <div>
            <div className="line" />
            Съставил: {doc.created_by_name ?? ''}
          </div>
        </div>

        <div className="print-foot">
          {form === 'proforma'
            ? 'Проформа фактурата не е данъчен документ и не подлежи на осчетоводяване.'
            : 'Документът е съставен от Тефтер — складова система, отговаряща на Приложение № 29 към Наредба Н-18.'}
        </div>

        {/* Само в края на съдържанието — на многостраничен документ пада
            естествено на последната страница, защото е единствен и е
            последен елемент в потока. */}
        <div className="print-banner">
          <img src="/brand/paper-banner.jpg" alt="Хартия А4 80гр — доставка" />
        </div>
      </div>
    </>
  );
}

function Row({ k, v }) {
  if (!v) return null;
  return <div className="row"><span className="k">{k}:</span><span>{v}</span></div>;
}

function FormLink({ id, form, current, disabled, children }) {
  const active = current === form;
  if (disabled) {
    return <button disabled title="Продажбата няма взет номер на фактура">{children}</button>;
  }
  return (
    <a href={`/print/document/${id}?form=${form}&print=1`}>
      <button className={active ? 'primary' : ''}>{children}</button>
    </a>
  );
}
